// Badges pour le tier B2B d'une organisation (starter|business|enterprise).
// Distinct de `subscriptionBadge.ts` qui couvre les tiers B2C
// (free|basic|pro|admin) — ne pas confondre les deux taxonomies.

const COLORS: Record<string, string> = {
  starter: "bg-sky-100 text-sky-800",
  business: "bg-indigo-100 text-indigo-800",
  enterprise: "bg-amber-100 text-amber-800",
};

// Tier inconnu = signal visuel (rouge) plutôt qu'un fallback silencieux.
const FALLBACK_COLOR = "bg-red-100 text-red-800";

/** Classes Tailwind pour un badge de tier B2B. */
export function tierBadgeColor(tier: string | null | undefined): string {
  if (!tier) return FALLBACK_COLOR;
  return COLORS[tier.toLowerCase()] ?? FALLBACK_COLOR;
}

/** Libellé lisible d'un tier B2B (Capitalisé), ou "inconnu" si absent. */
export function tierBadgeLabel(tier: string | null | undefined): string {
  if (!tier) return "inconnu";
  return tier.charAt(0).toUpperCase() + tier.slice(1);
}

// --------------------------------------------------------------------------
// Résolution du badge "type de compte" pour le composant shadcn <Badge>.
// Extrait de WorkerCell.tsx (DRY) — partagé entre la table worker, la liste
// des utilisateurs/abonnements et leurs vues détail. Distinct des helpers
// span ci-dessus (qui renvoient des classes Tailwind brutes).
// --------------------------------------------------------------------------

type BadgeVariant = "default" | "secondary" | "outline" | "destructive";

// Tier d'abonnement B2C legacy (subscription_type).
const TIER_VARIANT: Record<string, BadgeVariant> = {
  pro: "default",
  basic: "secondary",
  free: "outline",
  admin: "destructive",
};

// Tiers d'organisation B2B (Organization.tier). enterprise reçoit une classe
// ambre custom pour hiérarchiser visuellement starter < business < enterprise.
const ORG_TIER_VARIANT: Record<string, BadgeVariant> = {
  starter: "outline",
  business: "default",
  enterprise: "default",
};
export const ENTERPRISE_BADGE_CLASS =
  "border-transparent bg-amber-500 text-white hover:bg-amber-500/90";

/** Forme minimale requise pour résoudre le badge de tier (B2B prime sur B2C). */
export interface TierContext {
  is_business?: boolean | null;
  organization_tier?: string | null;
  subscription_type?: string | null;
}

export interface ResolvedTierBadge {
  label: string;
  variant: BadgeVariant;
  className?: string;
}

/**
 * Badge "type de compte". Pour une organisation B2B (is_business + tier), on
 * affiche le tier orga (starter/business/enterprise) — sinon le type
 * d'abonnement B2C legacy (pro/basic/free/admin). Corrige le "PRO" affiché à
 * tort sur une org business (dont subscription_type='pro' est backfillé).
 * Renvoie `null` quand aucune info n'est disponible (worker anonyme, etc.).
 */
export function resolveTierBadge(ctx?: TierContext | null): ResolvedTierBadge | null {
  if (ctx?.is_business && ctx?.organization_tier) {
    const t = ctx.organization_tier.toLowerCase();
    return {
      label: t,
      variant: ORG_TIER_VARIANT[t] ?? "outline",
      className: t === "enterprise" ? ENTERPRISE_BADGE_CLASS : undefined,
    };
  }
  const st = ctx?.subscription_type?.toLowerCase();
  if (!st) return null;
  return { label: st, variant: TIER_VARIANT[st] ?? "outline" };
}
